import { UseTellescopeQueryOptions } from "../../types";
import { useTellescopeQuery } from "../../use-tellescope-query";

export interface TwilioBillingRequest {
  /** ISO date (`YYYY-MM-DD`). Defaults to the start of the current month. */
  from?: string;
  /** ISO date (`YYYY-MM-DD`). Defaults to today. */
  to?: string;
}

export interface TwilioBillingResponse {
  data: {
    /** Marked-up grand total in dollars for the date range. No category breakdown is exposed to admins. */
    total: number;
    dateRange: {
      from: string;
      to: string;
    };
  };
}

export interface UseTwilioBillingQueryOptions<TError = Error, TData = TwilioBillingResponse>
  extends Omit<UseTellescopeQueryOptions<"integrations", "proxy_read", TError, TData>, "model" | "method" | "request"> {
  request?: TwilioBillingRequest;
}

/**
 * Fetches the org's Twilio spend for a date range as a single marked-up dollar total.
 *
 * This is the admin-scoped, deliberately redacted billing view — it returns only `total` and the
 * resolved `dateRange`, never a per-category (SMS / voice / phone-number) breakdown. The full
 * breakdown is superadmin-only and is not exposed through the SDK. Twilio billing is account-level,
 * so there is no per-phone-number filtering. Omitting `request` defaults to month-to-date.
 */
export const useTwilioBillingQuery = <TError = Error, TData = TwilioBillingResponse>({
  request,
  ...options
}: UseTwilioBillingQueryOptions<TError, TData> = {}) =>
  useTellescopeQuery({
    model: "integrations",
    method: "proxy_read",
    request: {
      integration: "Twilio",
      type: "billing",
      query: Object.values(request ?? {}).filter(Boolean).length ? JSON.stringify(request) : undefined,
    },
    ...options,
  });
